import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getCenterNoticeList,
    createCenterNotice,
    updateCenterNotice,
    deleteCenterNotice,
    getNoticePresignedUrls,
} from './api';
import { centerNoticeKeys } from './keys';
import {
    CreateCenterNoticeBody,
    UpdateCenterNoticeBody,
    PresignMediaContent,
} from './types';

export const useCenterNoticeList = ({
    centerId,
    page,
    limit,
    enabled = true,
}: {
    centerId: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: centerNoticeKeys.list(centerId, page, limit),
        queryFn: () => getCenterNoticeList({ centerId, page, limit }),
        enabled: enabled && !!centerId,
        select: ({ data }) => {
            if (
                !data || !Array.isArray(data.results) ||
                !Number.isSafeInteger(data.page) || data.page < 1 ||
                !Number.isSafeInteger(data.limit) || data.limit < 1 || data.limit > 100 ||
                !Number.isSafeInteger(data.totalPages) || data.totalPages < 0 ||
                !Number.isSafeInteger(data.totalResults) || data.totalResults < 0
            ) {
                throw new Error('공지 목록 응답을 확인할 수 없습니다.');
            }
            return data;
        },
    });

export const useNoticePresignedUrls = () => {
    return useMutation({
        mutationFn: ({
            centerId,
            mediaContents,
        }: {
            centerId: string;
            mediaContents: PresignMediaContent[];
        }) => getNoticePresignedUrls({ centerId, mediaContents }),
    });
};

export const useCreateCenterNotice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            body,
        }: {
            centerId: string;
            body: CreateCenterNoticeBody;
        }) => createCenterNotice({ centerId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: centerNoticeKeys.listAll(variables.centerId),
            });
        },
    });
};

export const useUpdateCenterNotice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            noticeId,
            body,
        }: {
            centerId: string;
            noticeId: string;
            body: UpdateCenterNoticeBody;
        }) => updateCenterNotice({ centerId, noticeId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: centerNoticeKeys.listAll(variables.centerId),
            });
        },
    });
};

export const useDeleteCenterNotice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            noticeId,
        }: {
            centerId: string;
            noticeId: string;
        }) => deleteCenterNotice({ centerId, noticeId }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: centerNoticeKeys.listAll(variables.centerId),
            });
        },
    });
};
